/**
 * Thread message de-duplication, shared by every surface that renders
 * `threadData.messages`.
 *
 * list-messages can return the same row more than once (the thread behind
 * INH-5600 returned 1,701 rows for 729 distinct messages), so the UI drops
 * repeats before rendering. A row is a repeat when it matches an earlier row
 * on id, message_type AND text — the text check is deliberate: a text_delta
 * snapshot and its persisted chat row share an id for a moment, and two
 * genuinely different messages that collide on id+type must both survive.
 *
 * The previous implementation was `filter` + `findIndex` (O(n²), ~44 ms per
 * pass on that thread) and was repeated inside every <ChatMessage>, so one
 * streamed frame cost ~57 passes ≈ 2.5 s of main-thread time. This version is
 * O(n) and memoised on the array identity, so repeated callers within one
 * render cycle share a single pass.
 */

export interface DedupableMessage {
  id?: string | number | null;
  message_type?: string | null;
  payload?: { text?: string | null; message_text?: string | null } | null;
  text?: string | null;
  [key: string]: unknown;
}

export const messageText = (m: DedupableMessage | null | undefined): string | null | undefined =>
  m?.payload?.text ?? m?.payload?.message_text ?? m?.text;

const cache = new WeakMap<object, DedupableMessage[]>();

const sameMessage = (a: DedupableMessage, b: DedupableMessage): boolean =>
  a.id === b.id && a.message_type === b.message_type && messageText(a) === messageText(b);

/**
 * Returns the first occurrence of every (id, message_type, text) triple, in
 * order. Returns the same array instance for the same input array instance.
 */
export const dedupMessages = <T extends DedupableMessage>(messages: T[] | null | undefined): T[] => {
  if (!Array.isArray(messages)) return [];
  const hit = cache.get(messages);
  if (hit) return hit as T[];

  const byId = new Map<unknown, T[]>();
  const out: T[] = [];
  for (const m of messages) {
    const key = m?.id ?? m;
    const bucket = byId.get(key);
    if (bucket) {
      if (bucket.some((seen) => sameMessage(seen, m))) continue;
      bucket.push(m);
    } else {
      byId.set(key, [m]);
    }
    out.push(m);
  }
  cache.set(messages, out);
  return out;
};
